import { getRestApiBearerToken } from './apiToken';
import { throwForExecuteResponse } from '../utils/billingErrors';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/**
 * @returns {Promise<{ rooms: Array<{ networkId: string, poolId: string, displayName: string, role: string, releaseVersion?: number }> }>}
 */
export async function fetchMyAgenticNetworks() {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/my/agentic-networks`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return { rooms: data.rooms || [] };
}

/**
 * Lightweight GET /networks/{id} (e.g. ``userId`` for Connections scope gating).
 *
 * @param {string} networkId
 * @returns {Promise<Record<string, unknown>>}
 */
export async function fetchNetworkSummary(networkId, { poolId } = {}) {
  const id = String(networkId || '').trim();
  if (!id) throw new Error('networkId is required');
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  // With ?poolId=<pool> a release-pinned room gets the FROZEN snapshot agents
  // (incl. the __roomContext__ Shared row: economyMode, agentMode, engine tiers)
  // — i.e. the config the room actually runs with, not the live draft.
  const pid = String(poolId || '').trim();
  // ``view=room`` (ROOMS.md §6a): only the fields the room reads — no chat history / graph / builds /
  // playbook / agent prompts (was ~260 KB + ~4 S3 reads on every room open). The workbench never sends it.
  const qs = `?view=room${pid ? `&poolId=${encodeURIComponent(pid)}` : ''}`;
  const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(id)}${qs}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const t = await res.text();
    throw new Error(t || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return data.network && typeof data.network === 'object' ? data.network : {};
}

/**
 * Release mode constants — keep the frontend vocabulary aligned with the
 * backend handler so a grep for either value lands in both places.
 */
export const RELEASE_MODE_NEW_POOL = 'NEW_POOL';
export const RELEASE_MODE_PATCH_EXISTING_POOLS = 'PATCH_EXISTING_POOLS';

const _TRIGGER_MODES_WIRE = new Set(['HEADLESS', 'HYBRID', 'MANUAL']);

function _normalizeSupervisorEmailsForWire(emails, ownerEmail) {
  if (!Array.isArray(emails)) return [];
  const exclude = new Set();
  if (typeof ownerEmail === 'string' && ownerEmail.trim()) {
    exclude.add(ownerEmail.trim().toLowerCase());
  }
  const seen = new Set();
  const out = [];
  for (const raw of emails) {
    const v = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    if (!v || seen.has(v) || exclude.has(v)) continue;
    seen.add(v);
    out.push(v);
  }
  return out;
}

/**
 * Shape the outbound request body for POST /networks/{id}/release.
 *
 * Accepts either:
 *   - A bare string (legacy): treated as the poolName for a NEW_POOL release.
 *   - An object ``{ mode, poolName?, poolIds?, ownerEmail?, supervisorEmails?, trigger? }``:
 *       * mode=NEW_POOL (default when a non-empty poolName is provided)
 *         → ``{ mode: "NEW_POOL", poolName, ...governance? }``
 *       * mode=PATCH_EXISTING_POOLS → ``{ mode: "PATCH_EXISTING_POOLS", poolIds: [...] }``
 *
 * Governance fields ride along on NEW_POOL only — patch mode never carries
 * them. ``ownerEmail`` is normalised to lowercase, ``supervisorEmails`` is
 * deduped + lowercased + has the owner removed (so the same identity never
 * appears twice on the wire), and ``trigger`` must be one of the canonical
 * three modes (HEADLESS / HYBRID / MANUAL). Empty / default values are
 * elided so the body stays compact and back-compat with pre-Phase-A handlers.
 *
 * Throws ``Error`` on shape violations so the UI surfaces a clear message
 * rather than the backend returning a generic 400. Kept pure (no network,
 * no token) so unit tests can exercise the entire validation table without
 * mocking fetch.
 *
 * @param {string | {
 *   mode?: string,
 *   poolName?: string,
 *   poolIds?: string[],
 *   ownerEmail?: string,
 *   supervisorEmails?: string[],
 *   trigger?: string,
 *   credentialOverrides?: Array<{ agentId?: string, instanceId?: string, configKey: string, value: string }>,
 * }} optionsOrPoolName
 * @returns {{
 *   mode: string,
 *   poolName?: string,
 *   poolIds?: string[],
 *   ownerEmail?: string,
 *   supervisorEmails?: string[],
 *   trigger?: string,
 *   credentialOverrides?: Array<{ agentId: string, instanceId: string, configKey: string, value: string }>,
 * }}
 */
export function buildReleaseRequestBody(optionsOrPoolName) {
  if (typeof optionsOrPoolName === 'string') {
    const poolName = optionsOrPoolName.trim();
    if (!poolName) {
      throw new Error('Pool name is required to release to a new pool.');
    }
    return { mode: RELEASE_MODE_NEW_POOL, poolName };
  }
  if (!optionsOrPoolName || typeof optionsOrPoolName !== 'object') {
    throw new Error('Release options object is required.');
  }
  const rawMode = typeof optionsOrPoolName.mode === 'string' ? optionsOrPoolName.mode.trim().toUpperCase() : '';
  const poolIds = Array.isArray(optionsOrPoolName.poolIds)
    ? optionsOrPoolName.poolIds.map((p) => (typeof p === 'string' ? p.trim() : '')).filter(Boolean)
    : [];
  const poolName = typeof optionsOrPoolName.poolName === 'string' ? optionsOrPoolName.poolName.trim() : '';

  // Determine effective mode: explicit wins; otherwise infer from payload.
  let mode = rawMode;
  if (!mode) {
    if (poolIds.length > 0) {
      mode = RELEASE_MODE_PATCH_EXISTING_POOLS;
    } else {
      mode = RELEASE_MODE_NEW_POOL;
    }
  }

  if (mode === RELEASE_MODE_PATCH_EXISTING_POOLS) {
    if (poolIds.length === 0) {
      throw new Error('Select at least one pool to patch.');
    }
    const patch = { mode, poolIds };
    // NOTE: versionRef is intentionally NOT whitelisted here (the upgrade-to-existing path carries it
    // separately — see the "does not leak versionRef through PATCH" contract test).
    // ROOM AGENT-OVERRIDE PRESERVATION (ROOM-SCOPES.md): the per-pool opt-out set — pools that should
    // take the new network graph fresh instead of keeping their owner disable/bypass carve. Subset of
    // poolIds; absent = preserve all (default). Whitelisted here or the backend never sees it.
    const resetIds = Array.isArray(optionsOrPoolName.resetAgentOverridesPoolIds)
      ? optionsOrPoolName.resetAgentOverridesPoolIds.map((p) => (typeof p === 'string' ? p.trim() : '')).filter(Boolean)
      : [];
    const _sel = new Set(poolIds);
    const _reset = resetIds.filter((p) => _sel.has(p));
    if (_reset.length > 0) patch.resetAgentOverridesPoolIds = _reset;
    return patch;
  }
  if (mode === RELEASE_MODE_NEW_POOL) {
    if (!poolName) {
      throw new Error('Pool name is required to release to a new pool.');
    }
    const body = { mode, poolName };
    const ownerEmail =
      typeof optionsOrPoolName.ownerEmail === 'string'
        ? optionsOrPoolName.ownerEmail.trim().toLowerCase()
        : '';
    if (ownerEmail) body.ownerEmail = ownerEmail;

    const supervisorEmails = _normalizeSupervisorEmailsForWire(
      optionsOrPoolName.supervisorEmails,
      ownerEmail,
    );
    if (supervisorEmails.length > 0) body.supervisorEmails = supervisorEmails;

    if (typeof optionsOrPoolName.trigger === 'string') {
      const t = optionsOrPoolName.trigger.trim().toUpperCase();
      if (t && !_TRIGGER_MODES_WIRE.has(t)) {
        throw new Error(`Unsupported trigger mode: ${optionsOrPoolName.trigger}`);
      }
      // Only emit non-default values to keep the wire body compact.
      if (t && t !== 'HYBRID') {
        body.trigger = t;
      }
    }

    // Phase 5: per-secret credential overrides. The Release dialog
    // surfaces every agent's MCP secrets that were stamped under the
    // workbench tier; the operator may type a fresh value to swap a
    // prod credential in instead of cloning the workbench value.
    // Only well-formed entries with a non-empty ``configKey`` + ``value``
    // make it onto the wire — empty rows are silently dropped so the
    // dialog can keep blank placeholder rows without polluting the body.
    const overrides = _normalizeCredentialOverridesForWire(
      optionsOrPoolName.credentialOverrides,
    );
    if (overrides.length > 0) {
      body.credentialOverrides = overrides;
    }

    // Version + Snapshot pin (Section 4). When the dialog's picker is on
    // an already-released ``vN``, the backend uses these to pin the new
    // pool to that exact version + snapshot instead of cutting a fresh
    // one — and to bypass the server-side no-op gate that would otherwise
    // refuse to release when topology + agent config haven't changed.
    if (typeof optionsOrPoolName.versionRef === 'string') {
      const v = optionsOrPoolName.versionRef.trim();
      if (v) body.versionRef = v;
    }
    if (typeof optionsOrPoolName.snapshotRef === 'string') {
      const s = optionsOrPoolName.snapshotRef.trim();
      if (s) body.snapshotRef = s;
    }

    // Connection provisioning mode (3-way): 'off' (default — provision nothing + isolate) |
    // 'room' (create the room's own shells) | 'network' (inherit). This MUST reach the wire: when
    // it was missing here the backend fell back to its legacy ``'room'`` default and seeded gitlab
    // shells even when the user picked None/Off. Legacy opt-out (``seedRoomTools:false``) still honored.
    const cm = String(optionsOrPoolName.connectionMode || '').trim().toLowerCase();
    if (['off', 'room', 'network'].includes(cm)) {
      body.connectionMode = cm;
    } else if (optionsOrPoolName.seedRoomTools === false) {
      body.seedRoomTools = false;
    }
    // Workspace bootstrap opt-out (default on) — same passthrough gap; forward the explicit ``false``.
    if (optionsOrPoolName.bootstrapWorkspace === false) {
      body.bootstrapWorkspace = false;
    }
    // "Start rooms with the suggested plan" (owner 2026-10-21) — THE wire whitelist (L: two shapers); true only.
    if (optionsOrPoolName.startWithSuggestedPlan === true) {
      body.startWithSuggestedPlan = true;
    }
    // §13 Lead/linked (PROJECT networks). This is the REAL wire whitelist — a field added only to
    // composeReleaseSubmitPayload is dropped here (the two-shaper trap). 'linked' + leadPoolId links
    // the new room to a chosen Lead (shared ledger + workspace); the backend validates + ignores it
    // on a non-project network.
    const roomKind = String(optionsOrPoolName.roomKind || '').trim().toLowerCase();
    const leadPoolId = String(optionsOrPoolName.leadPoolId || '').trim();
    if (roomKind === 'linked' && leadPoolId) {
      body.roomKind = 'linked';
      body.leadPoolId = leadPoolId;
    }
    // EXPERT ROOMS (EXPERT-ROOMS.md §4) — deploy an Expert Network as a team of rooms: {role: 'lead'} or
    // {role: 'department', team}. Whitelisted HERE (the real wire) or the backend never sees it.
    const xd = optionsOrPoolName.expertDeployment;
    if (xd && typeof xd === 'object') {
      const role = String(xd.role || '').trim().toLowerCase();
      // + the STAGE this team serves (EXPERT-ROOMS §15) — dev (training) when absent
      const stage = ['qa', 'prod'].includes(String(xd.stage || '').toLowerCase()) ? String(xd.stage).toLowerCase() : '';
      if (role === 'lead') body.expertDeployment = { role, ...(stage ? { stage } : {}) };
      else if (role === 'department' && String(xd.team || '').trim()) {
        body.expertDeployment = { role, team: String(xd.team).trim(), ...(stage ? { stage } : {}) };
      }
    }
    return body;
  }
  throw new Error(`Unsupported release mode: ${mode}`);
}

/**
 * Normalize the per-secret credential override list for wire transmission.
 *
 * Drops rows missing either ``configKey`` or ``value``, trims string
 * fields, and uses ``"*"`` as the wildcard sentinel for ``agentId`` /
 * ``instanceId`` so the backend can match either a specific tuple or
 * a fall-through (matching ``stepflow_core.pool_secret_clone``'s
 * lookup order).
 *
 * @param {unknown} raw
 * @returns {Array<{ agentId: string, instanceId: string, configKey: string, value: string }>}
 */
function _normalizeCredentialOverridesForWire(raw) {
  if (!Array.isArray(raw)) return [];
  /** @type {Array<{ agentId: string, instanceId: string, configKey: string, value: string }>} */
  const out = [];
  const seen = new Set();
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const configKey = typeof entry.configKey === 'string' ? entry.configKey.trim() : '';
    const value = typeof entry.value === 'string' ? entry.value : '';
    if (!configKey || !value) continue;
    const agentId = typeof entry.agentId === 'string' && entry.agentId.trim()
      ? entry.agentId.trim()
      : '*';
    const instanceId = typeof entry.instanceId === 'string' && entry.instanceId.trim()
      ? entry.instanceId.trim()
      : '*';
    const k = `${agentId}\u0000${instanceId}\u0000${configKey}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ agentId, instanceId, configKey, value });
  }
  return out;
}

/**
 * POST /networks/{id}/release
 *
 * Legacy call: ``releaseNetwork(networkId, "luca")``           → new pool
 * Structured:  ``releaseNetwork(networkId, { mode, poolName })`` or
 *              ``releaseNetwork(networkId, { mode, poolIds })``
 */
export async function releaseNetwork(networkId, optionsOrPoolName) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const body = buildReleaseRequestBody(optionsOrPoolName);
  const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/release`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data;
}

/**
 * GET /networks/{id}/pools — list the customer pools this network powers.
 * Used by the release dialog to offer a per-pool patch target picker.
 */
export async function listNetworkPools(networkId) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/pools`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return { pools: Array.isArray(data.pools) ? data.pools : [] };
}


/**
 * Set the network-wide default code connector (``runtimeConfig.defaultCodeConnectorSecretId``)
 * via ``PUT /networks/{id}``. The backend merges into the existing runtimeConfig, so
 * this is a safe partial update. Used by the single-pane connector step (§14) to bind
 * a network-scope connector against the live network — no room required.
 * @param {string} networkId
 * @param {string} secretId  a TESTED_OK code-connector registry secretId
 */
export async function setNetworkDefaultConnector(networkId, secretId) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/networks/${encodeURIComponent(networkId)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ runtimeConfig: { defaultCodeConnectorSecretId: String(secretId || '').trim() } }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data;
}


/**
 * POST /networks/{id}/pools/{poolId}/execute
 */
export async function poolExecute(networkId, poolId, body) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/pools/${encodeURIComponent(poolId)}/execute`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Throws ``IncludedTokensExceededError`` for 402 / IncludedTokensExceeded
    // so AgenticNetworkRoom can render the budget-exhausted UX; falls back
    // to a generic Error for any other failure mode.
    throwForExecuteResponse(res.status, data);
  }
  return data;
}

/**
 * POST /networks/{id}/pools/{poolId}/approval
 */
export async function poolApproval(networkId, poolId, { taskToken, decision, feedback, taskId }) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const payload = { taskToken, decision };
  if (feedback) payload.feedback = feedback;
  if (taskId) payload.taskId = taskId;
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}/pools/${encodeURIComponent(poolId)}/approval`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || data.error || `HTTP ${res.status}`);
  }
  return data;
}

// ----------------------------------------------------------------------------
// Pool context files (per-pool RULES/REFERENCES)
// ----------------------------------------------------------------------------

/**
 * Presign S3 PUT for a new pool context file.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ filename: string, contentType?: string, scope?: 'network'|'agent', agentId?: string, documentId?: string }} opts
 */
export async function getPoolContextUploadUrl(networkId, poolId, opts) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const qs = new URLSearchParams();
  qs.set('filename', opts.filename || '');
  if (opts.contentType) qs.set('contentType', opts.contentType);
  if (opts.scope) qs.set('scope', opts.scope);
  if (opts.agentId) qs.set('agentId', opts.agentId);
  if (opts.documentId) qs.set('documentId', opts.documentId);
  const url =
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}` +
    `/pools/${encodeURIComponent(poolId)}/context-files/upload-url?${qs.toString()}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Trigger hot or cold sync for an uploaded pool context file.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ s3Key: string, documentId: string, layer: 'hot'|'cold', scope?: 'network'|'agent', agentId?: string, filename?: string }} body
 */
export async function poolContextSync(networkId, poolId, body) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}` +
      `/pools/${encodeURIComponent(poolId)}/context-files/sync`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Unsync (delete) a pool context document from the hot or cold layer.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {string} documentId
 * @param {{ layer: 'hot'|'cold', scope?: 'network'|'agent', agentId?: string }} opts
 */
export async function poolContextUnsync(networkId, poolId, documentId, opts) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const qs = new URLSearchParams();
  qs.set('layer', opts.layer);
  if (opts.scope) qs.set('scope', opts.scope);
  if (opts.agentId) qs.set('agentId', opts.agentId);
  const url =
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}` +
    `/pools/${encodeURIComponent(poolId)}/context-files/${encodeURIComponent(documentId)}?${qs.toString()}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data;
}

/**
 * List pool context files (hot layer view; covers both hot + cold since they share a documentId).
 *
 * @param {string} networkId
 * @param {string} poolId
 */
export async function listPoolContextFiles(networkId, poolId) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(
    `${API_BASE_URL}/networks/${encodeURIComponent(networkId)}` +
      `/pools/${encodeURIComponent(poolId)}/context-files`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Convenience helper: presign → PUT file → POST sync (for both hot + cold if layer='both').
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {File} file
 * @param {{ scope?: 'network'|'agent', agentId?: string, layer?: 'hot'|'cold'|'both' }} [opts]
 */
export async function uploadAndSyncPoolContext(networkId, poolId, file, opts = {}) {
  const scope = opts.scope || 'network';
  const agentId = opts.agentId;
  const layer = opts.layer || 'both';
  const presign = await getPoolContextUploadUrl(networkId, poolId, {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    scope,
    agentId,
  });
  const putRes = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': presign.contentType || file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed (HTTP ${putRes.status})`);
  }
  const syncOne = (l) =>
    poolContextSync(networkId, poolId, {
      s3Key: presign.s3Key,
      documentId: presign.documentId,
      layer: l,
      scope,
      agentId,
      filename: presign.filename,
    });
  if (layer === 'both') {
    await Promise.all([syncOne('hot'), syncOne('cold')]);
  } else {
    await syncOne(layer);
  }
  return {
    documentId: presign.documentId,
    s3Key: presign.s3Key,
    filename: presign.filename,
    scope,
    agentId: agentId || null,
  };
}
